import { groupBy } from 'lodash';
import { scaleLinear } from '@visx/scale';
import {
  isScorable,
  makeRollupWeightFn,
  rollupWeightedScores,
} from 'js/utils/question-set';
import { mixColors, standardColor, toHexColor } from 'js/utils/color';

export const RADAR_DEPTH = {
  CATEGORIES: 'categories',
  QUESTION_SETS: 'questionSets',
  QUESTIONS: 'questions',
};

export const RADAR_DEPTH_OPTIONS = [
  { key: RADAR_DEPTH.CATEGORIES, label: 'Categories' },
  { key: RADAR_DEPTH.QUESTION_SETS, label: 'Question sets' },
  { key: RADAR_DEPTH.QUESTIONS, label: 'Questions' },
];

export const DEFAULT_SERIES_PALETTE = 'chart';
export const ASSESSMENT_SERIES_PALETTE = 'radar-assessments';
export const ASSESSMENT_SERIES_FILTER_MAX = 5;

export const RADAR_CURRENT_INK = standardColor();
export const RADAR_PREVIOUS_INK_BRIGHT = '#ff9f46';
export const RADAR_PREVIOUS_INK_DIM = mixColors(
  '#2c2c2c',
  RADAR_PREVIOUS_INK_BRIGHT,
  80
);

/**
 * Blue at 0 (current/latest), orange at 1 (first previous), dimmest at the
 * last sample. Domain is `[0, 1, max(lastIndex, 3)]` so 1–3 previous runs stay
 * on the same orange → midpoint → dimmest stops; more than 3 previous spreads
 * them evenly to the last index.
 */
export function assessmentSeriesInkScale(seriesCount) {
  return scaleLinear({
    domain: [0, 1, Math.max((seriesCount ?? 0) - 1, 3)],
    range: [
      RADAR_CURRENT_INK,
      RADAR_PREVIOUS_INK_BRIGHT,
      RADAR_PREVIOUS_INK_DIM,
    ],
  });
}

export function assessmentSeriesInkColor(index, seriesCount) {
  return toHexColor(assessmentSeriesInkScale(seriesCount)(Math.max(0, index)));
}

export function scorableQuestions(questionSet) {
  return Object.values(questionSet?.questions ?? {}).filter(isScorable);
}

export function listScorableQuestionSets(questionSets) {
  return Object.values(questionSets ?? {}).filter(
    (qs) => scorableQuestions(qs).length > 0
  );
}

export function questionSetsByCategory(questionSets) {
  return groupBy(listScorableQuestionSets(questionSets), (qs) => qs.category);
}

const MIN_RADAR_AXES = 3;

export function getAvailableRadarDepths(questionSets) {
  const sets = listScorableQuestionSets(questionSets);
  const categoryCount = new Set(sets.map((qs) => qs.category)).size;
  const questionCount = sets.reduce(
    (count, qs) => count + scorableQuestions(qs).length,
    0
  );

  const depths = [];
  if (categoryCount >= MIN_RADAR_AXES) {
    depths.push(RADAR_DEPTH.CATEGORIES);
  }
  if (sets.length >= MIN_RADAR_AXES) {
    depths.push(RADAR_DEPTH.QUESTION_SETS);
  }
  if (questionCount > 0) {
    depths.push(RADAR_DEPTH.QUESTIONS);
  }
  return depths;
}

export function getDefaultRadarDepth(availableDepths) {
  return availableDepths[0] ?? RADAR_DEPTH.QUESTIONS;
}

export function radarDepthLabel(depth) {
  return RADAR_DEPTH_OPTIONS.find((option) => option.key === depth)?.label;
}

function categoryLabel(category, categoryLookups) {
  return categoryLookups?.get?.(category)?.display_name ?? category;
}

function dummyRadarAxis(index) {
  return {
    key: `__dummy__${index}`,
    kind: 'dummy',
    label: '',
    dummy: true,
  };
}

function padRadarAxes(axes) {
  if (axes.length === 0 || axes.length >= MIN_RADAR_AXES) {
    return axes;
  }
  const padded = [...axes];
  while (padded.length < MIN_RADAR_AXES) {
    padded.push(dummyRadarAxis(padded.length));
  }
  return padded;
}

export function buildRadarAxes(
  questionSets,
  depth,
  { categoryLookups, categoryColorMap } = {}
) {
  const grouped = questionSetsByCategory(questionSets);
  let axes;

  if (depth === RADAR_DEPTH.CATEGORIES) {
    axes = Object.entries(grouped).map(([category, sets]) => ({
      key: category,
      kind: 'category',
      label: categoryLabel(category, categoryLookups),
      category,
      paletteClassName: categoryColorMap?.[category],
      questionSetKeys: sets.map((qs) => qs.key),
    }));
  } else if (depth === RADAR_DEPTH.QUESTION_SETS) {
    axes = Object.values(grouped)
      .flat()
      .map((qs) => ({
        key: qs.key,
        kind: 'questionSet',
        label: qs.displayName,
        category: qs.category,
        paletteClassName: categoryColorMap?.[qs.category],
        questionSetKey: qs.key,
      }));
  } else {
    axes = Object.values(grouped)
      .flat()
      .flatMap((qs) =>
        scorableQuestions(qs).map((question) => ({
          key: `${qs.key}:${question.key}`,
          kind: 'question',
          label: question.name || question.text,
          detailLabel: question.text || question.name,
          category: qs.category,
          paletteClassName: categoryColorMap?.[qs.category],
          questionSetKey: qs.key,
          questionKey: question.key,
        }))
      );
  }

  return padRadarAxes(axes);
}

export function scoreForRadarAxis(questionSets, axis, depth) {
  if (axis?.dummy || axis?.kind === 'dummy') {
    return 0;
  }

  if (depth === RADAR_DEPTH.CATEGORIES) {
    const sets = (axis.questionSetKeys ?? [])
      .map((key) => questionSets?.[key])
      .filter(Boolean);
    return rollupWeightedScores(sets, {
      getScore: (qs) => qs.normalizedScore,
      getWeight: makeRollupWeightFn(sets),
    });
  }

  if (depth === RADAR_DEPTH.QUESTION_SETS) {
    return questionSets?.[axis.questionSetKey]?.normalizedScore ?? null;
  }

  return (
    questionSets?.[axis.questionSetKey]?.questions?.[axis.questionKey]
      ?.normalizedScore ?? null
  );
}

export function scoresForRadarAxes(questionSets, axes, depth) {
  return axes.map((axis) => scoreForRadarAxis(questionSets, axis, depth));
}

function finiteScores(values) {
  return values.filter((value) => value != null && !Number.isNaN(value));
}

function contributingScoresForRadarAxis(questionSets, axis, depth) {
  if (axis?.dummy || axis?.kind === 'dummy') {
    return [0];
  }

  if (depth === RADAR_DEPTH.CATEGORIES) {
    return finiteScores(
      (axis.questionSetKeys ?? []).map(
        (key) => questionSets?.[key]?.normalizedScore
      )
    );
  }

  if (depth === RADAR_DEPTH.QUESTION_SETS) {
    return finiteScores(
      scorableQuestions(questionSets?.[axis.questionSetKey]).map(
        (question) => question.normalizedScore
      )
    );
  }

  const question =
    questionSets?.[axis.questionSetKey]?.questions?.[axis.questionKey];
  return finiteScores([question?.minScore, question?.maxScore]);
}

export function rangeForRadarAxis(questionSets, axis, depth) {
  const scores = contributingScoresForRadarAxis(questionSets, axis, depth);
  if (!scores.length) {
    return { min: null, max: null };
  }
  return { min: Math.min(...scores), max: Math.max(...scores) };
}

export function rangesForRadarAxes(questionSets, axes, depth) {
  return axes.map((axis) => rangeForRadarAxis(questionSets, axis, depth));
}

export function applySeriesPalette(fields, paletteName) {
  const items = fields ?? [];
  if (!paletteName || paletteName === DEFAULT_SERIES_PALETTE) {
    return items;
  }
  return items.map((field, index) => ({
    ...field,
    paletteIndex: index + 1,
    ...(paletteName === ASSESSMENT_SERIES_PALETTE
      ? { inkColor: assessmentSeriesInkColor(index, items.length) }
      : {}),
  }));
}

export function seriesPaletteClasses(paletteName, paletteIndex) {
  const name = paletteName || DEFAULT_SERIES_PALETTE;
  const paletteClassName = `palette-${name}-${paletteIndex}`;
  if (name === DEFAULT_SERIES_PALETTE) {
    return {
      paletteClassName,
      mutedPaletteClassName: `palette-chart-dark-${paletteIndex}`,
    };
  }
  if (name === ASSESSMENT_SERIES_PALETTE) {
    return {
      paletteClassName,
      mutedPaletteClassName: paletteClassName,
    };
  }
  return {
    paletteClassName,
    mutedPaletteClassName: `${paletteClassName} palette-muted`,
  };
}

export function seriesItemsFromLegendMaps(legendFields, seriesDataMaps) {
  return (legendFields ?? []).map((field) => ({
    key: field.key,
    name: field.name,
    paletteIndex: field.paletteIndex,
    disabled: Boolean(field.disabled),
    questionSets: seriesDataMaps?.[field.key]?.questionSets ?? {},
  }));
}

export function questionSetsFromDataMap(dataMap, assessmentId) {
  if (!dataMap) {
    return {};
  }
  return assessmentId
    ? dataMap.assessments?.[assessmentId]?.questionSets ?? {}
    : dataMap.questionSets ?? {};
}

export function defaultSeriesFilterKeys(items = [], maxSeries = 0) {
  const keys = items.map((item) => item.key).filter(Boolean);
  if (maxSeries > 0 && keys.length > maxSeries) {
    return keys.slice(0, maxSeries);
  }
  return keys;
}

export function reconcileSeriesFilterKeys(
  currentKeys = [],
  items = [],
  maxSeries = 0
) {
  const available = new Set(items.map((item) => item.key));
  const next = currentKeys.filter((key) => available.has(key));
  if (next.length === 0) {
    return defaultSeriesFilterKeys(items, maxSeries);
  }
  if (maxSeries > 0 && next.length > maxSeries) {
    return next.slice(0, maxSeries);
  }
  return next;
}
